import { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { PtoVolumePosition } from '@shared/api';
import { IconCheck, IconCopy } from '@shared/ui/icons';
import { positionLinkText, positionLinkUrl, rowLabel } from './rowRef';

/** Номер и кнопка в строку; в тесной таблице кнопка уходит под номер — графа не
 *  отнимает ширину у соседних. */
const Wrap = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 4px;
`;

const No = styled.span`
  font-weight: 600;
  white-space: nowrap;
`;

/** Номер строки — кнопкой: открывает лист строки в окне исходного листа РД. */
const NoLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
  color: inherit;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    border-radius: ${({ theme }) => theme.radius.xs};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const CopyLink = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

interface Props {
  position: PtoVolumePosition;
  /** Шифр комплекта — в начало текста ссылки; null — ещё не загружен. */
  docCode: string | null;
  projectId: string;
  revisionId: string;
  /** Открыть лист строки в окне исходного листа РД. */
  onOpenSheet: (p: PtoVolumePosition) => void;
}

/** «№»: метка номера строки («СП01 №5», «ИИ №3») или «—» и «Скопировать ссылку» —
 *  текст «<шифр>, СП01 №5 — <наименование>, СП01 л. 2 (стр. PDF 56)» и адрес строки
 *  на этом экране. Клик по номеру открывает лист строки в окне листа РД. Графа
 *  видна всем, кто видит экран. */
export function RowNoCell({ position: p, docCode, projectId, revisionId, onOpenSheet }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  async function copy() {
    const text = positionLinkText(p, docCode);
    const url = positionLinkUrl(window.location.origin, projectId, revisionId, p);
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
    } catch {
      // Буфер обмена недоступен (страница не по HTTPS, нет разрешения) — текст
      // для копирования вручную.
      window.prompt('Скопируйте ссылку на строку:', `${text} ${url}`);
    }
  }

  const title = copied ? 'Ссылка скопирована' : 'Скопировать ссылку на строку';
  const label = rowLabel(p);
  return (
    <Wrap>
      {label && p.page_number > 0 ? (
        <NoLink type="button" title="Открыть лист РД со строкой" onClick={() => onOpenSheet(p)}>
          {label}
        </NoLink>
      ) : (
        <No>{label ?? '—'}</No>
      )}
      <CopyLink type="button" title={title} aria-label={title} onClick={() => void copy()}>
        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
      </CopyLink>
    </Wrap>
  );
}
